import { useTranslation } from "react-i18next";
import {
  ArrowRight,
  Calendar,
  Check,
  Megaphone,
  HeartHandshake,
  Users,
  ClipboardCheck,
  Layers,
  Workflow,
  BarChart3,
  ShieldCheck,
} from "lucide-react";
import HeroTiles from "@/components/HeroTiles";
import ShareCTA from "@/components/ShareCTA";
import usePageHead from "@/hooks/usePageHead";
import { scrollToId } from "@/utils/scroll";

const AREA_ICONS = [
  Megaphone,
  HeartHandshake,
  Users,
  ClipboardCheck,
  Layers,
  Workflow,
  BarChart3,
  ShieldCheck,
];

export default function Home() {
  const { t } = useTranslation();
  usePageHead("home");

  const stats = t("home.stats", { returnObjects: true });
  const steps = t("home.steps", { returnObjects: true });
  const deliverable = t("home.deliverable.items", { returnObjects: true });
  const areas = t("home.areas.items", { returnObjects: true });
  const whoItems = t("home.who.items", { returnObjects: true });
  const timeline = t("home.timeline.items", { returnObjects: true });

  const scrollToHowItWorks = (e) => {
    e.preventDefault();
    scrollToId("how-it-works");
  };

  return (
    <div data-testid="home-page">
      {/* Section 1: Hero */}
      <section className="section hero" data-testid="section-hero">
        <div className="container hero__grid">
          <div className="hero__content">
            <p className="eyebrow eyebrow--blue">{t("home.hero.eyebrow")}</p>
            <h1 className="display-heading hero__title">{t("home.hero.h1")}</h1>
            <p className="body-large hero__lede">{t("home.hero.lede")}</p>
            <p className="hero__meta">
              <Calendar size={20} strokeWidth={1.5} aria-hidden="true" />
              <span>{t("home.hero.meta")}</span>
            </p>
            <div className="hero__actions">
              <ShareCTA className="btn btn--primary" data-testid="hero-primary-cta" aria-label={`${t("home.hero.primaryCta")} ${t("common.newTabSuffix")}`}>
                <span>{t("home.hero.primaryCta")}</span>
                <ArrowRight size={16} strokeWidth={1.5} aria-hidden="true" />
              </ShareCTA>
              <a href="#how-it-works" className="btn btn--secondary" onClick={scrollToHowItWorks} data-testid="hero-see-how">
                <span>{t("home.hero.secondaryCta")}</span>
                <ArrowRight size={16} strokeWidth={1.5} aria-hidden="true" />
              </a>
            </div>
          </div>
          <div className="hero__visual">
            <HeroTiles />
          </div>
        </div>
      </section>

      {/* Section 2: What the program is */}
      <section className="section section--gray" id="about-the-program" data-testid="section-what">
        <div className="container two-col">
          <div className="two-col__aside">
            <h2 className="section-heading">{t("home.what.h2")}</h2>
          </div>
          <div className="two-col__main">
            <p className="body-large what__para">{t("home.what.p1")}</p>
            <p className="body-large what__para">{t("home.what.p2")}</p>
            <div className="stat-cards">
              {stats.map((s, i) => (
                <div className="stat-card" key={i}>
                  <p className="stat-card__title">{s.title}</p>
                  <p className="stat-card__caption">{s.caption}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Section 3: How it works */}
      <section className="section" id="how-it-works" data-testid="section-how">
        <div className="container">
          <h2 className="section-heading how__heading">{t("home.how.h2")}</h2>
          <div className="steps">
            {steps.map((step, i) => (
              <div className="step" key={i}>
                <span className="step__num">{`0${i + 1}`}</span>
                <h3 className="card-heading step__title">{step.title}</h3>
                <p className="step__body">{step.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Section 4: The deliverable */}
      <section className="section section--dark" data-testid="section-deliverable">
        <div className="container two-col">
          <div className="two-col__aside">
            <p className="eyebrow eyebrow--on-dark">{t("home.deliverable.eyebrow")}</p>
            <h2 className="section-heading section-heading--light">{t("home.deliverable.h2")}</h2>
            <p className="body-large deliverable__intro">{t("home.deliverable.intro")}</p>
          </div>
          <div className="two-col__main">
            <ol className="deliverable-list">
              {deliverable.map((item, i) => (
                <li className="deliverable-item" key={i}>
                  <h3 className="card-heading deliverable-item__title">{item.title}</h3>
                  <p className="deliverable-item__body">{item.body}</p>
                </li>
              ))}
            </ol>
          </div>
        </div>
      </section>

      {/* Section 5: Challenge areas */}
      <section className="section" data-testid="section-areas">
        <div className="container">
          <h2 className="section-heading">{t("home.areas.h2")}</h2>
          <p className="body-large areas__intro">{t("home.areas.intro")}</p>
          <div className="area-grid">
            {areas.map((area, i) => {
              const Icon = AREA_ICONS[i];
              const examples = area.examples || [];
              return (
                <div className="area-card" key={i} data-testid="area-card">
                  <Icon className="area-card__icon" size={32} strokeWidth={1.5} aria-hidden="true" />
                  <h3 className="card-heading area-card__title">{area.title}</h3>
                  <ul className="area-card__examples">
                    {examples.map((ex, j) => (
                      <li key={j} className="area-card__example">
                        <span className="area-card__arrow" aria-hidden="true">→</span>
                        <span>{ex}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Section 6: Who should apply */}
      <section className="section section--blue" data-testid="section-who">
        <div className="container two-col">
          <div className="two-col__aside">
            <h2 className="section-heading">{t("home.who.h2")}</h2>
            <p className="body-large who__para">{t("home.who.p")}</p>
            <p className="body-large who__para">{t("home.who.p2")}</p>
          </div>
          <div className="two-col__main">
            <ul className="checklist">
              {whoItems.map((item, i) => (
                <li className="checklist__item" key={i}>
                  <Check className="checklist__icon" size={20} strokeWidth={1.5} aria-hidden="true" />
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* Section 7: Timeline */}
      <section className="section" data-testid="section-timeline">
        <div className="container">
          <h2 className="section-heading timeline__heading">{t("home.timeline.h2")}</h2>
          <ol className="timeline">
            {timeline.map((node, i) => (
              <li className="timeline__node" key={i}>
                <span className="timeline__marker" aria-hidden="true" />
                <p className="timeline__label">{node.label}</p>
                <p className="timeline__body">{node.body}</p>
              </li>
            ))}
          </ol>
        </div>
      </section>

      {/* Section 8: Final CTA band */}
      <section className="section section--dark cta-band" data-testid="section-cta">
        <div className="container cta-band__inner">
          <h2 className="section-heading section-heading--light cta-band__title">{t("home.cta.h2")}</h2>
          <p className="body-large cta-band__para">{t("home.cta.p")}</p>
          <ShareCTA className="btn btn--primary cta-band__btn" data-testid="cta-primary" aria-label={`${t("home.cta.finalBandLabel")} ${t("common.newTabSuffix")}`}>
            <span>{t("home.cta.finalBandLabel")}</span>
            <ArrowRight size={16} strokeWidth={1.5} aria-hidden="true" />
          </ShareCTA>
        </div>
      </section>
    </div>
  );
}
